/* eslint-disable lingui/no-unlocalized-strings */
// Privacy policy — port of design/source/public/privacy.html.
// Chrome rendered by HomeLayout — do not add NavBar/Footer here.
import { t } from "@lingui/macro";
import { Link } from "react-router";

import type { LegalSection } from "@/client/components/public/legal-layout";
import { LegalLayout } from "@/client/components/public/legal-layout";
import { SEO } from "@/client/components/seo";

const SECTIONS: LegalSection[] = [
  {
    id: "tldr",
    title: "TL;DR",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>We collect what we need to find jobs and tailor resumes for you — nothing more.</li>
        <li>
          Your resume content, applications, and saved jobs are <strong>yours</strong>. We
          don&apos;t sell them. We don&apos;t train public models on them.
        </li>
        <li>
          AI processing happens on Anthropic and OpenAI infrastructure under no-training agreements.
        </li>
        <li>
          You can export everything as JSON or delete your account permanently from{" "}
          <span className="font-mono text-[12.5px]">Settings → Privacy</span> at any time.
        </li>
        <li>
          We&apos;re a UAE company. We honor GDPR, UK GDPR, and CCPA rights regardless of where you
          live.
        </li>
      </ul>
    ),
  },
  {
    id: "data-we-collect",
    title: "1. What we collect",
    body: (
      <>
        <p>
          <strong>You give us:</strong> name, email, work history, skills, education, locations,
          salary expectations, the resumes and cover letters you build, and any documents you
          upload.
        </p>
        <p>
          <strong>You connect:</strong> with your permission, profile data from LinkedIn
          (read-only), Google Drive (resume files), and email forwarding addresses you set up for
          application replies.
        </p>
        <p>
          <strong>We observe:</strong> pages you visit inside Apply Edge, features you use, plan and
          billing events, IP, browser, and device fingerprint (for fraud and login security only).
        </p>
        <p>
          <strong>We don&apos;t:</strong> read your email inbox, harvest your contacts, install
          browser extensions you didn&apos;t approve, or pull location beyond city-level from IP.
        </p>
      </>
    ),
  },
  {
    id: "data-we-dont",
    title: "2. What we never collect",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          Passwords to job sites. Apply Assist uses our own integrations and OAuth — never your
          credentials.
        </li>
        <li>
          Government IDs, biometrics, or financial information beyond what Stripe needs for billing.
        </li>
        <li>Personal info about your references unless you paste it into a resume yourself.</li>
      </ul>
    ),
  },
  {
    id: "use",
    title: "3. How we use it",
    body: (
      <>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <strong>Run the product.</strong> Match jobs, tailor resumes, send applications, surface
            stats.
          </li>
          <li>
            <strong>Improve quality.</strong> Aggregated, anonymized usage signals (e.g.
            &quot;tailoring lifts response rate by 3.4× in this market&quot;) inform features. Your
            resume text is <em>never</em> in this dataset.
          </li>
          <li>
            <strong>Communicate.</strong> Account emails, weekly digests (you can turn off), and the
            occasional product announcement.
          </li>
          <li>
            <strong>Bill you.</strong> Process payments via Stripe.
          </li>
          <li>
            <strong>Stay safe.</strong> Detect fraud, abuse, and account takeovers.
          </li>
        </ol>
        <p>
          We do <strong>not</strong> use your resume content, application history, or job
          preferences to train AI models — ours or anyone else&apos;s. Anthropic and OpenAI process
          inference requests under zero-retention agreements: your prompts and outputs are not
          retained beyond the API call and not used for their model training.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "4. Who we share with",
    body: (
      <>
        <p>Only the processors we need to run the service:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Anthropic, OpenAI</strong> — AI inference (no training, no retention)
          </li>
          <li>
            <strong>AWS (eu-central-1, us-east-1)</strong> — hosting and storage
          </li>
          <li>
            <strong>Stripe</strong> — payments
          </li>
          <li>
            <strong>Postmark</strong> — transactional email
          </li>
          <li>
            <strong>Sentry, PostHog</strong> — error monitoring and product analytics (self-hosted,
            EU region)
          </li>
        </ul>
        <p>
          We don&apos;t sell, rent, or share your data with advertisers, data brokers, recruiters,
          or third-party marketers.
        </p>
        <p>
          If a recruiter or company contacts you through an application you sent, we share only the
          materials you submitted (resume, cover letter, contact details on the application). We do
          not share your other applications, browsing history, or saved jobs.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "5. How long we keep it",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          <strong>Active account:</strong> as long as you keep using Apply Edge.
        </li>
        <li>
          <strong>After cancel/downgrade:</strong> resumes and applications stay available on Free
          indefinitely; oldest items archive (read-only) past Free&apos;s caps.
        </li>
        <li>
          <strong>After account deletion:</strong> personal data wiped within 30 days. Backups roll
          over within 90 days. Anonymized aggregate analytics may persist.
        </li>
        <li>
          <strong>Billing records:</strong> retained 7 years for tax compliance.
        </li>
      </ul>
    ),
  },
  {
    id: "rights",
    title: "6. Your rights",
    body: (
      <>
        <p>Wherever you live, you can:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Access</strong> — export everything as JSON from Settings → Privacy
          </li>
          <li>
            <strong>Correct</strong> — edit anything in-app, or email us
          </li>
          <li>
            <strong>Delete</strong> — one click in Settings → Privacy. Permanent. We won&apos;t ask
            why.
          </li>
          <li>
            <strong>Portability</strong> — JSON export includes resumes, cover letters,
            applications, and notes
          </li>
          <li>
            <strong>Object</strong> — opt out of analytics and product emails from Settings →
            Notifications
          </li>
          <li>
            <strong>Withdraw consent</strong> — for any optional integration, anytime
          </li>
        </ul>
        <p>
          For GDPR/UK GDPR or CCPA-specific requests, write to{" "}
          <a className="text-primary hover:underline" href="mailto:privacy@apply-edge.com">
            privacy@apply-edge.com
          </a>
          . We respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "7. Security",
    body: (
      <>
        <p>
          Encryption in transit (TLS 1.3) and at rest (AES-256). SOC 2 Type II audit underway,
          completion expected Q3 2026. Production access is MFA-gated, role-scoped, and logged. We
          run quarterly penetration tests and a continuous bug bounty.
        </p>
        <p>
          Full details:{" "}
          <Link to="/security" className="text-primary hover:underline">
            apply-edge.com/security
          </Link>
        </p>
      </>
    ),
  },
  {
    id: "transfers",
    title: "8. International transfers",
    body: (
      <p>
        Apply Edge is operated from the UAE with primary infrastructure in the EU (Frankfurt) and a
        US replica (Virginia) for latency. EU/UK personal data crosses borders only under Standard
        Contractual Clauses with all subprocessors. Data residency on a specific region is available
        on Ultimate.
      </p>
    ),
  },
  {
    id: "children",
    title: "9. Children",
    body: (
      <p>
        Apply Edge is not intended for users under 16. We don&apos;t knowingly collect data from
        children. If you believe a child has signed up, write to us and we&apos;ll delete the
        account.
      </p>
    ),
  },
  {
    id: "changes",
    title: "10. Changes to this policy",
    body: (
      <p>
        If we change anything material, we&apos;ll email every active user at least 30 days before
        it takes effect. Cosmetic and clarifying edits are listed in the changelog at the top.
      </p>
    ),
  },
  {
    id: "contact",
    title: "11. Contact",
    body: (
      <>
        <p>
          Privacy questions:{" "}
          <a className="text-primary hover:underline" href="mailto:privacy@apply-edge.com">
            privacy@apply-edge.com
          </a>
        </p>
        <p>
          Data Protection Officer:{" "}
          <a className="text-primary hover:underline" href="mailto:dpo@apply-edge.com">
            dpo@apply-edge.com
          </a>
        </p>
        <p className="mt-2 text-[12.5px] text-muted-foreground">
          Apply Edge (entity formation in progress), Dubai, UAE (registered address pending)
        </p>
      </>
    ),
  },
];

export const PrivacyPage = () => (
  <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
    <SEO
      title={t`Privacy policy — Apply Edge`}
      description={t`Apply Edge privacy policy. Your data is yours: we don't sell it, don't train on it, and you can delete it whenever you want.`}
      canonical="/privacy"
    />
    <LegalLayout
      titleHead="Privacy"
      titleEmph="policy"
      meta="Last updated April 27, 2026 · Effective May 11, 2026"
      summary={{
        label: "In one breath",
        body: "Your data is yours. We don't sell it, don't train on it, and you can delete it whenever you want.",
      }}
      sections={SECTIONS}
    />
  </div>
);
